import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { Selection } from "react-aria-components";

import type { BatchCosignItem } from "@/features/cosignature/types";

import type { AdaptedQueueRow } from "./(table)/helpers/adapters";

type SelectionActions = {
  clearSelection: VoidFunction;
  selectRows: (rows: AdaptedQueueRow[]) => void;
};

type SelectionStateValue = {
  selectedItems: BatchCosignItem[];
  selectedKeys: Selection;
};

// split contexts: reset triggers (tabs/filters) consume only the stable actions and
// stay out of the selection re-render path; the table/batch bar consume the state.
const SelectionActionsContext = createContext<Nullable<SelectionActions>>(null);
const SelectionStateContext = createContext<Nullable<SelectionStateValue>>(null);

const EMPTY_ROWS: AdaptedQueueRow[] = [];

function toBatchItem({ documentType, id, patientId }: AdaptedQueueRow): BatchCosignItem {
  return { documentId: id, documentType, patientId };
}

export function CosignatureSelectionProvider({ children }: { children: ReactNode }) {
  const [selectedRows, setSelectedRows] = useState<AdaptedQueueRow[]>(EMPTY_ROWS);

  const selectRows = useCallback((rows: AdaptedQueueRow[]) => setSelectedRows(rows), []);
  const clearSelection = useCallback(() => setSelectedRows(EMPTY_ROWS), []);

  const actions = useMemo<SelectionActions>(
    () => ({ clearSelection, selectRows }),
    [clearSelection, selectRows],
  );

  // memoized so SelectionStateContext consumers don't re-render on parent
  // re-renders unrelated to selection (guards the split-context pattern)
  const state = useMemo<SelectionStateValue>(
    () => ({
      selectedItems: selectedRows.map(toBatchItem),
      selectedKeys: new Set(selectedRows.map((row) => row.id)),
    }),
    [selectedRows],
  );

  return (
    <SelectionActionsContext.Provider value={actions}>
      <SelectionStateContext.Provider value={state}>{children}</SelectionStateContext.Provider>
    </SelectionActionsContext.Provider>
  );
}

export function useCosignatureSelectionActions() {
  const ctx = useContext(SelectionActionsContext);

  if (!ctx) {
    throw new Error(
      "useCosignatureSelectionActions must be used within CosignatureSelectionProvider",
    );
  }

  return ctx;
}

export function useCosignatureSelectionState() {
  const ctx = useContext(SelectionStateContext);

  if (!ctx) {
    throw new Error(
      "useCosignatureSelectionState must be used within CosignatureSelectionProvider",
    );
  }

  return ctx;
}
